import React, {useCallback, useEffect, useState} from "react";
import {ACHIEVEMENT_IMAGES} from "../util/Constants";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import PlayerNameWithFlag from "../components/players/PlayerNameWithFlag";
import {Dropdown, OverlayTrigger, Popover, ProgressBar, Tab, Tabs} from "react-bootstrap";
import MatchBox from "../components/matches/MatchBox";
import TournamentResultCard from "../components/players/profile/TournamentResultCard";
import EloChart from "../components/players/profile/EloChart";
import {formatDate} from "../util/StringUtils";
import {getLoggedUser, isAdmin, resetPassword} from "../util/SecurityUtils";
import Button from "react-bootstrap/Button";
import UpdatePlayerModal from "../components/players/modals/UpdatePlayerModal";
import Pageable from "../components/common/Pageable";
import {useIsMobile} from "../hooks/useIsMobile";
import PlayerLadderSelect from "../components/common/PlayerLadderSelect";
import ChangePasswordModal from "../components/players/modals/ChangePasswordModal";
import ConfirmModal from "../components/common/ConfirmModal";
import {usePlayer} from "../hooks/queries/usePlayer";
import {useUpdatePlayer} from "../hooks/mutations/usePlayerMutations";
import type {Player, PlayerAchievement, PlayerResult, Match} from "../types/domain";
import type {ReactNode} from "react";
import {ACHIEVEMENT_IMAGES as ACHIEVEMENT_IMAGES_T} from "../util/Constants";

interface CompactAchievement {
  id: number;
  achievement: string;
  priority: number;
  count: number;
  dates: string[];
}

function PlayerDetail() {
    const { id } = useParams()
    const { t, i18n } = useTranslation();
    const isMobile = useIsMobile();
    const { data: player } = usePlayer(id);
    const updatePlayerMutation = useUpdatePlayer();
    const [ showUpdatePlayerForm, setShowUpdatePlayerForm ] = useState(false)
    const [ ladderId, setLadderId ] = useState<number | null>(null)
    const [ showChangePasswordModal, setShowChangePasswordModal ] = useState(false)
    const [ showResetPasswordModal, setShowResetPasswordModal ] = useState(false)

    useEffect(() => {
        if (player && player.playerStatistics.length > 0 && ladderId == null) {
            setLadderId(player.playerStatistics[0].ladder.id);
        }
    }, [player, ladderId])

    const stats = player?.playerStatistics.find(s => s.ladder.id === ladderId) ?? null

    const getEloEvolutionData = () => {
      const eloArray = []
      if (stats != null && stats.eloChartPoints.length > 0) {
          eloArray.push({
              name: t("startingEloShort"),
              y: 1200,
              color: "lightgreen"
          })

          stats.eloChartPoints.forEach( point => {
              eloArray.push({
                  name: formatDate(point.date) + "<br/>" + point.opponent,
                  y: point.elo,
                  color: point.win ? "lightgreen" : "red"
              })
          })
      } else {
          eloArray.push({
              name: t("startingEloShort"),
              y: (player as any).elo ?? 1200,
              color: "lightgreen"
          })
      }
      return eloArray;
    }

    const getAchievementPopover = (achievement: CompactAchievement) => {
        return (
          <Popover key={"achievement-popover-" + achievement.id} className="achievement-popover">
              <Popover.Header as="h3">{t("achievementTitle__" + achievement.achievement)}</Popover.Header>
              <Popover.Body>
                  <p className="achievement-popover-description"><i>{t("achievementDescription__" + achievement.achievement)}</i></p>
                  <div className="achievement-popover-dates">
                      {achievement.dates.map((date: string) => (
                          <strong key={"achievement-date-" + achievement.achievement + date}>{formatDate(date)}</strong>
                      ))}
                  </div>
              </Popover.Body>
          </Popover>
        )
    }

    const getPercentilePopover = () => {
        return (
          <Popover style={{maxHeight: "12rem"}}>
              <Popover.Header as="h3">{t("averagePercentileHeader")}</Popover.Header>
              <Popover.Body>
                  {t("averagePercentileDescription")}
              </Popover.Body>
          </Popover>
        )
    }

    const handleUpdatePlayer = useCallback(async (player: Partial<Player> & { id: number }) => {
        await updatePlayerMutation.mutateAsync(player);
        setShowUpdatePlayerForm(false);
    }, [updatePlayerMutation])

    const handleReset = useCallback(async (userId: number) => {
        await resetPassword(userId).then(response => {
            if (response) {
                setShowResetPasswordModal(false)
                // TODO Success notification?
            } else {
                // TODO Error notification?
            }
        })
    }, [setShowResetPasswordModal])

    const setLadder = (ladder: number) => {
        setLadderId(ladder)
    }

    const getStats = (showTitle = false) => {
        return (
          <div>
              { showTitle ? <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                  <h3>{t("statistics")}</h3>
              </div> : null }
              <div className="left-half-responsive">
                  <div className="left-half">
                      <div className="mt-2 mb-4">
                          <h5>{t("rank")}:</h5><h1>{stats.eloRank}/{stats.totalPlayers}</h1>
                      </div>
                      <div className="mt-2 mb-4">
                          <h5>{t("elo")}:</h5><h1>{stats.elo}</h1>
                      </div>
                  </div>
                  <div className="right-half">
                      <div className="mt-2 mb-4">
                          <h5>{t("tournaments")}:</h5><h1>{stats.tournamentsPlayed}</h1>
                      </div>
                      <div className="mt-2 mb-4">
                          <h5>Ø Rating:</h5><h1>{(stats.averageRating ?? 0).toFixed(2)}</h1>
                      </div>
                      {/*<div className="mt-2 mb-4">*/}
                      {/*    <h5>{t("averagePercentile")}:&nbsp;*/}
                      {/*    <OverlayTrigger placement="bottom" overlay={getPercentilePopover()}>*/}
                      {/*        <span style={{textDecoration: "underline", fontSize: "1rem", fontWeight: "normal"}}>(?)</span>*/}
                      {/*    </OverlayTrigger></h5>*/}
                      {/*    <h1>{stats.averagePlacementPercentile == "NaN" ? "-" : stats.averagePlacementPercentile.toFixed(2)}</h1>*/}
                      {/*</div>*/}
                  </div>
              </div>
              <div className="right-half-responsive">
                  <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                      {t("matches")}
                  </div>
                  <ProgressBar max={200}>
                      <ProgressBar label={stats.matchesWon} variant="success" now={stats.matchesWon} key={1} max={stats.matchesPlayed > 0 ? stats.matchesPlayed : 1} />
                      <ProgressBar label={stats.matchesLost} variant="danger" now={stats.matchesLost} key={2} max={stats.matchesPlayed > 0 ? stats.matchesPlayed : 1} />
                  </ProgressBar>
                  <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                      {t("sets")}
                  </div>
                  <ProgressBar max={200}>
                      <ProgressBar label={stats.setsWon} variant="success" now={stats.setsWon} key={1} max={stats.setsPlayed > 0 ? stats.setsPlayed : 1} />
                      <ProgressBar label={stats.setsLost} variant="danger" now={stats.setsLost} key={2} max={stats.setsPlayed > 0 ? stats.setsPlayed : 1} />
                  </ProgressBar>
                  <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                      {t("points")}
                  </div>
                  <ProgressBar max={200}>
                      <ProgressBar label={stats.pointsWon} variant="success" now={stats.pointsWon} key={1} max={stats.pointsPlayed > 0 ? stats.pointsPlayed : 1} />
                      <ProgressBar label={stats.pointsLost} variant="danger" now={stats.pointsLost} key={2} max={stats.pointsPlayed > 0 ? stats.pointsPlayed : 1} />
                  </ProgressBar>
              </div>
          </div>
        )
    }

    const getTournaments = (showTitle: boolean, itemsPerPage: number, maxPages: number) => {
        const TournamentContainer = ({ children }: { children: ReactNode }) => (
          <div className="flex-wrap d-flex gap-4 pb-4">{children}</div>
        );
        return (
          <>
              { showTitle ? <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                  <h3>{t("tournaments")}</h3>
              </div> : null }
              {player!.tournamentResults.length === 0 ? <h6>{t("noTournamentsYet")}</h6> : null}
              <Pageable items={player!.tournamentResults} itemsPerPage={itemsPerPage} maxPages={maxPages}
                        ItemContainer={TournamentContainer}
                        renderItem={(item: PlayerResult) => <TournamentResultCard key={"tournament-card-" + item.tournament.id} result={item} />}  />
          </>
        )
    }

    const getMatches = (showTitle: boolean, itemsPerPage: number, maxPages: number) => {
        return (
          <>
              { showTitle ? <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                  <h3>{t("matches")}</h3>
              </div> : null }
              <Pageable items={player!.matches} itemsPerPage={itemsPerPage} maxPages={maxPages}
                        renderItem={(item: Match) => <MatchBox variant="profile" key={"match-box-" + item.id} match={item} ratingId={id == null ? null : Number(id)} />}
              />
              {player!.matches.length === 0 ? <h6>{t("noMatchesYet")}</h6> : null}
          </>
        )
    }

    const getEloChart = (showTitle = false) => {
        return (
          <>
              { showTitle ?
              <div className="mt-2 mb-2" style={{textAlign: "center"}}>
                  <h3>{t("eloEvolution")}</h3>
              </div> : null }
              <EloChart data={getEloEvolutionData()} />
          </>
        )
    }
    
    const getAchievements = (showTitle = false) => {
        const compactAchievements: CompactAchievement[] = []
        for (const achievement of (stats?.achievements ?? []) as PlayerAchievement[]) {
            const a = compactAchievements.find(a => a.achievement === achievement.achievement)
            if (a) {
                a.count++
                a.dates.push(achievement.createdAt)
            } else {
                compactAchievements.push({
                    id: achievement.id,
                    achievement: achievement.achievement,
                    priority: achievement.priority,
                    count: 1,
                    dates: [achievement.createdAt]
                })
            }
        }
        return (
          <div className="flex-wrap achievement-row">
              { showTitle ? <h3 className="mt-1">{t("achievements")}</h3> : null }
              {compactAchievements.sort((a, b) => a.priority - b.priority).map(achievement => {
                  return (
                    <OverlayTrigger key={"achievement-" + achievement.id} placement="bottom" overlay={getAchievementPopover(achievement)}>
                        <div className="achievement-image-profile" data-count={"×" + achievement.count}>
                            <img src={ACHIEVEMENT_IMAGES[achievement.achievement as keyof typeof ACHIEVEMENT_IMAGES_T]} className="achievement-image" alt={achievement.achievement} />
                        </div>
                    </OverlayTrigger>
                  )})}
          </div>
        )
    }

    const getLadderControlButtons = () => (
        <PlayerLadderSelect
            playerStatistics={player.playerStatistics}
            selectedLadderId={ladderId}
            onChange={setLadder}
        />
    );

    const getMobilePageLayout = () => {
        return (
          <Tabs
            defaultActiveKey="achievements-and-stats"
            id="player-detail-tabs"
            variant="tabs"
            fill
          >
              <Tab eventKey="achievements-and-stats" title={t("achievementsAndStats")}>
                  { getLadderControlButtons() }
                  { getAchievements(true) }
                  { getStats(true) }
                  { getEloChart(true) }
              </Tab>
              <Tab eventKey="tournaments" title={t("tournaments")} className="mt-3">
                  { getTournaments(false, 2, 9) }
              </Tab>
              <Tab eventKey="matches" title={t("matches")} className="mt-3">
                  { getMatches(false, 5, 9) }
              </Tab>
          </Tabs>
        )
    }

    const getDesktopPageLayout = () => {
        return (
          <>
              { getLadderControlButtons() }
              { getAchievements(true) }
              <div>
                  <div className="left-half-responsive" style={{"height": "300px"}}>
                      { getStats(true) }
                  </div>
                  <div className="right-half-responsive" style={{"height": "300px"}}>
                      { getEloChart(true) }
                  </div>
              </div>
              <div>
                  <div className="left-half-responsive">
                      { getTournaments(true, 6, 15) }
                  </div>
                  <div className="right-half-responsive">
                      { getMatches(true, 7, 15) }
                  </div>
              </div>
          </>
        )
    }

    const isCurrentUser = getLoggedUser()?.id === player?.userId

    return !player ? <div/> : (
      <div className="base-container">
        <div className="table-header header">
            <div>
                <span style={{width: "12rem", display: "inline-block"}}>{t("playerProfile")}:</span>
                <PlayerNameWithFlag player={player} includeElo={false} color={"white"} />
            </div>
            <div className="header-with-buttons ms-auto">
                {isMobile ?
                  <>
                  <Dropdown>
                      <Dropdown.Toggle variant="outline-light" id="dropdown-basic" className="w-100 mb-3">
                          {t("actions")}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                          {isCurrentUser && <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>{t("changePassword")}</Dropdown.Item> }
                          {isAdmin() && !isCurrentUser && player?.userId &&
                            <Dropdown.Item variant="danger" onClick={() => setShowResetPasswordModal(true)}>{t("resetPassword")}</Dropdown.Item>
                          }
                          {isAdmin() && <Dropdown.Item variant="light" onClick={() => setShowUpdatePlayerForm(true)}>{t("editPlayer")}</Dropdown.Item> }
                      </Dropdown.Menu>
                  </Dropdown>
                  </> : <>
                      {isCurrentUser ?
                        <Button variant="info" onClick={() => setShowChangePasswordModal(true)} className="table-button">
                            {t("changePassword")}
                        </Button>
                        : null }
                      {isAdmin() && !isCurrentUser && player?.userId ?
                        <Button variant="danger" onClick={() => setShowResetPasswordModal(true)} className="table-button">
                            {t("resetPassword")}
                        </Button>
                        : null }
                      {isAdmin() ?
                        <Button variant="light" onClick={() => setShowUpdatePlayerForm(true)} className="table-button">
                            {t("editPlayer")}
                        </Button>
                        : null }
                  </>
                }
            </div>
        </div>
        {stats ?
            <div className="content">
                { isMobile ? getMobilePageLayout() : getDesktopPageLayout() }
            </div> : null}
          <UpdatePlayerModal player={player} isOpen={showUpdatePlayerForm} closeModal={() => setShowUpdatePlayerForm(false)} handleSubmit={handleUpdatePlayer} />
          <ChangePasswordModal isOpen={showChangePasswordModal} closeModal={() => setShowChangePasswordModal(false)} />
          {showResetPasswordModal &&
              <ConfirmModal title={t("resetPasswordOf", {username: player?.username})} message={t("resetPasswordConfirmation")}
                            onCancel={() => setShowResetPasswordModal(false)}
                            onConfirm={() => handleReset(player.userId)}/>
          }
      </div>
    );
}

export default PlayerDetail
